import { PerformanceViewByEnum } from 'components/BusinessActivity/PerformanceFilters';
import { ServicesFormValues } from 'components/Contracts/types';
import { ProductsFormValues } from 'components/Locations/types';
import { formatPrice, PriceAndFees } from 'components/Products/PayoutCard/utils';
import env from 'env';
import { isEqual } from 'lodash';
import Script from 'next/script';
import { CountryCode } from 'types/config';
import bindMethods from 'utils/bindMethods';
import { getFullBasePath } from 'utils/router';
import { Event, EventCategory, Page, PageCategory, Variables } from './types';

const separators = {
  comma: ', ',
  pipe: ' | ',
  slash: ' / ',
  semicolon: '; ',
};

const requiredVariables: Array<keyof Variables> = [
  'site_env',
  'site_browser',
  'site_country',
  'site_type',
  'site_sourcepage',
  'site_lang',
  'site_platform',
  'site_resolution',
];

const gtm = {
  hasRequiredVariables: false,
  variables: {
    site_env: undefined,
    site_browser: undefined,
    site_country: undefined,
    site_type: undefined,
    site_sourcepage: undefined,
    site_ciam_id: undefined,
    site_company_id: undefined,
    site_lang: undefined,
    site_platform: undefined,
    site_resolution: undefined,
  } as Partial<Variables>,
  countryCode: undefined as undefined | CountryCode,

  init(gtmId?: string, countryCode?: CountryCode, hasRequiredVariables?: boolean | null) {
    // country specific
    if (countryCode) this.countryCode = countryCode;
    if (hasRequiredVariables !== null && hasRequiredVariables !== undefined)
      this.hasRequiredVariables = hasRequiredVariables;

    // to disable GTM on local
    if (!gtmId || !this.hasRequiredVariables || env.env === 'local') return null;
    return (
      <Script id="google-tag-manager" strategy="afterInteractive">
        {`(function (w, d, s, l, i) {
        w[l] = w[l] || [${JSON.stringify(this.variables)}];
        w[l].push({'gtm.start':new Date().getTime(),event:'gtm.js'});
        var f = d.getElementsByTagName(s)[0];
        var j = d.createElement(s);
        var dl = l != 'dataLayer' ? '&l=' + l : '';
        j.async = true;
        j.src = 'https://www.googletagmanager.com/gtm.js?id=' + i + dl;
        f.parentNode.insertBefore(j, f);
      })(window, document, 'script', 'dataLayer', '${gtmId}')`}
      </Script>
    );
  },
  setVariables(variables: Partial<Variables>) {
    if (!this.hasRequiredVariables) {
      this.variables = { ...this.variables, ...variables };
      return requiredVariables.every((key) => Boolean(this.variables[key]) ?? false);
    } else {
      const newVariables = { ...this.variables, ...variables };
      if (!isEqual(newVariables, this.variables)) {
        this.variables = newVariables;
        this.push(this.variables);
      }
      return null;
    }
  },
  track(event: Event) {
    const fullPath = getFullBasePath();
    this.push({
      ...event,
      event: 'gtm.custom_event',
      event_label:
        fullPath && event.event_label.startsWith('/')
          ? `${fullPath}${event.event_label}`
          : event.event_label,
    });
  },
  push(data: Record<string, any>) {
    // TODO: remove when NNGM-487 tested
    if (env.env !== 'local' && env.env !== 'production') console.log(data);

    if (window !== undefined && window.dataLayer !== undefined) {
      window.dataLayer.push(data);
    }
  },
  // Events
  trackClickSupport() {
    this.track({
      event_name: 'click_support',
      event_category: EventCategory.Support,
      page_category: PageCategory.Home,
      event_label: '/support',
    });
  },
  trackSupportSearch(event_search_label: string, event_results_number: number) {
    this.track({
      event_name: 'support_search',
      event_category: EventCategory.Support,
      page_category: PageCategory.FAQ,
      event_label: '/support/search',
      event_results_number,
      event_search_label,
    });
  },
  trackClickContactUs(event_position: 'Nav' | 'Question' | 'Bottom', event_faq_label?: string) {
    this.track({
      event_name: 'click_contact_us',
      event_category: EventCategory.Support,
      page_category: PageCategory.FAQ,
      event_label: '/contact-us',
      event_position,
      event_faq_label,
    });
  },
  trackClickSubmitContactForm(
    event_topic: string[],
    event_subtopic: string[],
    event_message?: string,
  ) {
    this.track({
      event_name: 'click_submit_contact_form',
      event_category: EventCategory.Support,
      page_category: PageCategory.ContactForm,
      event_label: '/contact-us',
      event_topic: event_topic.join() || undefined,
      event_subtopic: event_subtopic.join() || undefined,
      event_message,
    });
  },
  trackClickSelectLanguage(event_language: string) {
    this.track({
      event_name: 'click_select_language',
      event_category: EventCategory.LanguageSelection,
      page_category: PageCategory.Home,
      event_label: '/',
      event_language,
    });
  },
  trackClickLogin() {
    this.track({
      event_name: 'click_login',
      event_category: EventCategory.Login,
      page_category: PageCategory.Home,
      event_label: env.sodexoConnectUrl,
    });
  },
  trackClickManageCompany() {
    this.track({
      event_name: 'click_manage_company',
      event_category: EventCategory.ManageCompany,
      page_category: PageCategory.Home,
      event_label: '/dashboard',
    });
  },
  trackClickRegisterCompany() {
    this.track({
      event_name: 'click_register_company',
      event_category: EventCategory.RegisterCompany,
      page_category: PageCategory.Home,
      event_label: '/affiliation',
    });
  },
  trackClickRegisterLocation() {
    // TODO: implement when Vlad returns from a vacation
  },
  trackClickFindMe(event_message_code?: string, event_message?: string) {
    this.track({
      event_name: 'click_find_me',
      event_category: EventCategory.RegisterCompany,
      page_category: PageCategory.CompanyID,
      event_label: '/affiliation',
      event_message_code: event_message_code ?? 'OK',
      event_message: event_message ?? 'OK',
    });
  },
  trackClickCompanyConfirm(
    event_activity_selected: string[],
    event_secondary_activity_selected: string[],
  ) {
    this.track({
      event_name: 'click_company_confirm',
      event_category: EventCategory.RegisterCompany,
      page_category: PageCategory.CompanyInfo,
      event_label: '/affiliation',
      event_activity_selected: event_activity_selected.join(separators.pipe) || undefined,
      event_secondary_activity_selected:
        event_secondary_activity_selected.join(separators.pipe) || undefined,
    });
  },
  trackClickCompanyBack(
    event_activity_selected: string[],
    event_secondary_activity_selected: string[],
  ) {
    this.track({
      event_name: 'click_company_back',
      event_category: EventCategory.RegisterCompany,
      page_category: PageCategory.CompanyInfo,
      event_label: '/affiliation',
      event_activity_selected: event_activity_selected.join(separators.pipe) || undefined,
      event_secondary_activity_selected:
        event_secondary_activity_selected.join(separators.pipe) || undefined,
    });
  },
  trackClickContactConfirm(event_contact_position: string[], event_contact_language?: string[]) {
    this.track({
      event_name: 'click_contact_confirm',
      event_category: EventCategory.RegisterCompany,
      page_category: PageCategory.Contact,
      event_label: '/affiliation',
      event_contact_position: event_contact_position.join(separators.pipe) || undefined,
      ...(this.countryCode === 'be'
        ? { event_contact_language: event_contact_language?.join(separators.pipe) || undefined }
        : {}),
    });
  },
  trackClickContactBack(event_contact_position: string[], event_contact_language?: string[]) {
    this.track({
      event_name: 'click_contact_back',
      event_category: EventCategory.RegisterCompany,
      page_category: PageCategory.Contact,
      event_label: '/affiliation',
      event_contact_position: event_contact_position.join(separators.pipe) || undefined,
      ...(this.countryCode === 'be'
        ? { event_contact_language: event_contact_language?.join(separators.pipe) || undefined }
        : {}),
    });
  },
  trackClickProductSelect(event_product_name: string, event_product_activity: string[] = []) {
    this.track({
      event_name: 'click_product_select',
      event_category: EventCategory.RegisterCompany,
      page_category: PageCategory.Product,
      event_label: '/affiliation',
      event_product_name,
      event_product_activity: event_product_activity.join(separators.pipe) || undefined,
    });
  },
  trackClickProductOfferSelect(
    event_product_name: string,
    { price, fees, feesByKey }: PriceAndFees,
    event_payout_frequency?: string | null,
    event_payout_channel?: string | null,
    event_product_tier?: string,
  ) {
    const priceFormatted = formatPrice(price);
    this.track({
      event_name: 'click_product_offer_select',
      event_category: EventCategory.RegisterCompany,
      page_category: PageCategory.Product,
      event_label: '/affiliation',
      event_product_name,
      ...(this.countryCode === 'cz'
        ? {
            event_payout_frequency: event_payout_frequency || undefined,
            event_payout_channel: event_payout_channel || undefined,
            // see components/Products/PayoutCard/CzPayoutCard.tsx
            event_payout_commission: priceFormatted
              ? `${priceFormatted}${
                  fees.length > 0
                    ? `${separators.pipe}${fees.map((p) => formatPrice(p)).join(separators.slash)}`
                    : ''
                }`
              : undefined,
          }
        : {}),
      ...(this.countryCode === 'be'
        ? {
            event_product_tier: event_product_tier || undefined,
            // see components/Products/PayoutCard/BePayoutCard.tsx
            event_payout_commission: [
              [
                formatPrice(feesByKey['trxCutOff'] ? feesByKey['trxCostAboveCutOff'] : price, '-'),
                formatPrice(feesByKey['monthlyFee'], '-'),
                formatPrice(feesByKey['activationFee'], '-'),
                feesByKey['trxCutOff']?.fee > 0
                  ? // <cutOff: price (minCost)
                    `<${formatPrice(feesByKey['trxCutOff'], '0')}: ${formatPrice(
                      price,
                      '-',
                    )} (${formatPrice(feesByKey['trxMinCost'], '-')})`
                  : '-',
              ].join(separators.slash),
              formatPrice(feesByKey['onlinePrice'], '-'),
            ].join(separators.pipe),
          }
        : {}),
    });
  },
  trackClickProductOfferApply(
    event_product_name: string,
    { price, fees, feesByKey }: PriceAndFees,
    event_payout_frequency?: string | null,
    event_payout_channel?: string | null,
    event_product_tier?: string,
  ) {
    const priceFormatted = formatPrice(price);
    this.track({
      event_name: 'click_product_offer_apply',
      event_category: EventCategory.RegisterCompany,
      page_category: PageCategory.Product,
      event_label: '/affiliation',
      event_product_name,
      ...(this.countryCode === 'cz'
        ? {
            event_payout_frequency: event_payout_frequency ?? undefined,
            event_payout_channel: event_payout_channel ?? undefined,
            // see components/Products/PayoutCard/CzPayoutCard.tsx
            event_payout_commission: priceFormatted
              ? `${priceFormatted}${
                  fees.length > 0
                    ? `${separators.pipe}${fees.map((p) => formatPrice(p)).join(separators.slash)}`
                    : ''
                }`
              : undefined,
          }
        : {}),
      ...(this.countryCode === 'be'
        ? {
            event_product_tier: event_product_tier || undefined,
            // see components/Products/PayoutCard/BePayoutCard.tsx
            event_payout_commission: [
              [
                formatPrice(feesByKey['trxCutOff'] ? feesByKey['trxCostAboveCutOff'] : price, '-'),
                formatPrice(feesByKey['monthlyFee'], '-'),
                formatPrice(feesByKey['activationFee'], '-'),
                feesByKey['trxCutOff']?.fee > 0
                  ? // <cutOff: price (minCost)
                    `<${formatPrice(feesByKey['trxCutOff'], '0')}: ${formatPrice(
                      price,
                      '-',
                    )} (${formatPrice(feesByKey['trxMinCost'], '-')})`
                  : '-',
              ].join(separators.slash),
              formatPrice(feesByKey['onlinePrice'], '-'),
            ].join(separators.pipe),
          }
        : {}),
    });
  },
  trackClickProductSelectConfirm(event_selected_product_number: number) {
    this.track({
      event_name: 'click_product_select_confirm',
      event_category: EventCategory.RegisterCompany,
      page_category: PageCategory.Product,
      event_label: '/affiliation',
      event_selected_product_number,
    });
  },
  trackClickProductSelectBack() {
    this.track({
      event_name: 'click_product_select_back',
      event_category: EventCategory.RegisterCompany,
      page_category: PageCategory.Product,
      event_label: '/affiliation',
    });
  },
  trackClickProductDelete(event_product_name: string) {
    this.track({
      event_name: 'click_product_delete',
      event_category: EventCategory.RegisterCompany,
      page_category: PageCategory.Product,
      event_label: '/affiliation',
      event_product_name,
    });
  },
  trackClickProductEdit(event_product_name: string) {
    this.track({
      event_name: 'click_product_edit',
      event_category: EventCategory.RegisterCompany,
      page_category: PageCategory.Product,
      event_label: '/affiliation',
      event_product_name,
    });
  },
  trackClickPaymentConfirm() {
    this.track({
      event_name: 'click_payment_confirm',
      event_category: EventCategory.RegisterCompany,
      page_category: PageCategory.Payment,
      event_label: '/affiliation',
    });
  },
  trackClickPaymentBack() {
    this.track({
      event_name: 'click_payment_back',
      event_category: EventCategory.RegisterCompany,
      page_category: PageCategory.Payment,
      event_label: '/affiliation',
    });
  },
  trackClickContractConfirm() {
    this.track({
      event_name: 'click_contract_confirm',
      event_category: EventCategory.RegisterCompany,
      page_category: PageCategory.Contract,
      event_label: '/onboarding',
    });
  },
  trackClickContractBack() {
    this.track({
      event_name: 'click_contract_back',
      event_category: EventCategory.RegisterCompany,
      page_category: PageCategory.Contract,
      event_label: '/affiliation',
    });
  },
  trackClickAffiliationStepper(page_category: PageCategory) {
    this.track({
      event_name: 'click_stepper',
      event_category: EventCategory.RegisterCompany,
      page_category,
      event_label: page_category === PageCategory.Location ? '/onboarding' : '/affiliation',
    });
  },
  // BEL-only
  trackClickLocationSelectType(event_location_type: string) {
    this.track({
      event_name: 'click_location_selecttype',
      event_category: EventCategory.RegisterCompany,
      page_category: PageCategory.Location,
      event_label: '/onboarding',
      event_location_type: event_location_type?.toLowerCase(),
    });
  },
  trackClickLocationAddTerminal() {
    this.track({
      event_name: 'click_location_addterminal',
      event_category: EventCategory.RegisterCompany,
      page_category: PageCategory.Location,
      event_label: '/onboarding',
    });
  },
  trackClickLocationConfirm(
    params:
      | {
          event_location_name: string;
          event_activity: string[];
          event_accepted_product: string[];
          event_payment_option: Array<{ name?: string; options?: string[][] }>;
          event_terminal_created_number?: number;
          event_activation_date?: string;
        }
      | {
          event_location_name: string;
          event_activity: string[][];
          event_accepted_product: string[];
          event_terminal_definition: Array<{ deviceType: string; products: string[] }>;
        }
      | null,
  ) {
    this.track({
      event_name: 'click_location_confirm',
      event_category: EventCategory.RegisterCompany,
      page_category: PageCategory.Location,
      event_label: '/onboarding',
      ...(params
        ? 'event_payment_option' in params
          ? {
              event_location_name: params.event_location_name || undefined,
              event_activity: params.event_activity.join(separators.pipe) || undefined,
              event_accepted_product:
                params.event_accepted_product.join(separators.pipe) || undefined,
              event_payment_option:
                params.event_payment_option
                  .map((o) => {
                    const options = o.options
                      ?.filter((op) => op.some((i) => i))
                      ?.map((op) => op.join(separators.comma))
                      ?.join(separators.semicolon);
                    return `${o.name}${o.options ? `:${options ? ` ${options}` : ''}` : ''}`;
                  })
                  .join(separators.pipe) || undefined,
              event_terminal_created_number: params.event_terminal_created_number,
              event_activation_date: params.event_activation_date,
            }
          : {
              event_location_name: params.event_location_name || undefined,
              event_activity:
                params.event_activity
                  .map((row) => row.join(separators.semicolon))
                  .join(separators.pipe) || undefined,
              event_accepted_product:
                params.event_accepted_product.join(separators.pipe) || undefined,
              event_terminal_definition:
                params.event_terminal_definition
                  .map((def) => `${def.deviceType}: ${def.products.join(separators.semicolon)}`)
                  .join(separators.pipe) || undefined,
              event_terminal_created_number: params.event_terminal_definition.length,
            }
        : {}),
    });
  },
  trackClickLocationAnother() {
    this.track({
      event_name: 'click_location_another',
      event_category: EventCategory.RegisterCompany,
      page_category: PageCategory.Location,
      event_label: '/onboarding',
    });
  },
  trackClickLocationFinish() {
    this.track({
      event_name: 'click_location_finish',
      event_category: EventCategory.RegisterCompany,
      page_category: PageCategory.Location,
      event_label: '/onboarding',
    });
  },
  trackClickRegistrationFinish(event_label = '/dashboard') {
    this.track({
      event_name: 'click_registration_finish',
      event_category: EventCategory.RegisterCompany,
      page_category: PageCategory.Finished,
      event_label,
    });
  },
  /*
   * FEATURED events
   * They have slightly different structure.
   * naming convention: Action + Page + Feature
   */
  /* SUPPORT */
  trackClickSupportShowAnswer(event_faq_question: string) {
    this.track({
      event_name: 'click',
      feature: 'show_answer',
      event_category: EventCategory.Support,
      page: Page.Support,
      page_category: PageCategory.Home,
      event_faq_question,
      event_label: '/support',
    });
  },
  /* DASHBOARD */
  trackClickDashboardScreenView() {
    this.track({
      event_name: 'click',
      feature: 'screen_view',
      event_category: EventCategory.SelfCare,
      page: Page.Dashboard,
      page_category: PageCategory.Dashboard,
      event_label: '/dashboard',
    });
  },
  trackClickDashboardInfotextSeeMore(event_label: string) {
    this.track({
      event_name: 'click',
      feature: 'InfoText_see_more',
      event_category: EventCategory.SelfCare,
      page: Page.Dashboard,
      page_category: PageCategory.Dashboard,
      event_label,
    });
  },
  trackClickDashboardInfotextCarousel(
    event_label: string,
    infotext: string | undefined,
    position: number,
  ) {
    this.track({
      event_name: 'click',
      feature: 'InfoText_carousel',
      event_category: EventCategory.SelfCare,
      page: Page.Dashboard,
      page_category: PageCategory.Dashboard,
      event_label,
      infotext,
      position,
    });
  },
  trackClickDashboardBanner(event_label: string, banner?: string) {
    this.track({
      event_name: 'click',
      feature: 'Banner_detail',
      event_category: EventCategory.SelfCare,
      page: Page.Dashboard,
      page_category: PageCategory.Dashboard,
      event_label,
      banner,
    });
  },
  trackClickDashboardBannerCarousel(
    event_label: string,
    banner: string | undefined,
    position: number,
  ) {
    this.track({
      event_name: 'click',
      feature: 'Banner_detail',
      event_category: EventCategory.SelfCare,
      page: Page.Dashboard,
      page_category: PageCategory.Dashboard,
      event_label,
      banner,
      position,
    });
  },
  trackClickDashboardRemittanceSheetSeeMore() {
    this.track({
      event_name: 'click',
      feature: 'RemittanceSheet_see_more',
      event_category: EventCategory.SelfCare,
      page: Page.Dashboard,
      page_category: PageCategory.Dashboard,
      event_label: '/remittance-sheet',
    });
  },
  trackClickDashboardOnlinePaymentSeeMore() {
    this.track({
      event_name: 'click',
      feature: 'OnlinePayments_see_more',
      event_category: EventCategory.SelfCare,
      page: Page.Dashboard,
      page_category: PageCategory.Dashboard,
      event_label: '/payments',
    });
  },
  trackClickDashboardFaqSeeMore() {
    this.track({
      event_name: 'click',
      feature: 'Faq',
      event_category: EventCategory.SelfCare,
      page: Page.Dashboard,
      page_category: PageCategory.Dashboard,
      event_label: '/support',
    });
  },
  trackClickDashboardFaqQuestion(question_title: string) {
    this.track({
      event_name: 'click',
      feature: 'Faq_question',
      event_category: EventCategory.SelfCare,
      page: Page.Dashboard,
      page_category: PageCategory.Dashboard,
      event_label: `/support/search/${question_title}`,
      question_title,
    });
  },
  trackClickDashboardPerformanceSeeMore() {
    this.track({
      event_name: 'click',
      feature: 'Performance_see_more',
      event_category: EventCategory.SelfCare,
      page: Page.Dashboard,
      page_category: PageCategory.Dashboard,
      event_label: '/performance',
    });
  },
  trackClickDashboardPerformancePeriod(period: PerformanceViewByEnum) {
    this.track({
      event_name: 'click',
      feature: 'Performance_period',
      event_category: EventCategory.SelfCare,
      page: Page.Dashboard,
      page_category: PageCategory.Dashboard,
      event_label: '/performance',
      period: period === PerformanceViewByEnum.Year ? 'yearly' : 'monthly',
    });
  },
  trackClickDashboardPerformanceAmount(amount: string) {
    this.track({
      event_name: 'click',
      feature: 'Performance_amount',
      event_category: EventCategory.SelfCare,
      page: Page.Dashboard,
      page_category: PageCategory.Dashboard,
      event_label: '/performance',
      amount: amount === 'totalAmount' ? 'Total' : amount === 'cardsAmount' ? 'Card' : 'Voucher',
    });
  },
  trackClickDashboardPerformanceLocation(location?: string) {
    this.track({
      event_name: 'click',
      feature: 'Performance_location',
      event_category: EventCategory.SelfCare,
      page: Page.Dashboard,
      page_category: PageCategory.Dashboard,
      event_label: '/performance',
      location: location || 'All',
    });
  },
  trackClickDashboardInvoicesSeeMore() {
    this.track({
      event_name: 'click',
      feature: 'Invoices_see_more',
      event_category: EventCategory.SelfCare,
      page: Page.Dashboard,
      page_category: PageCategory.Dashboard,
      event_label: '/invoices',
    });
  },
  trackClickDashboardTransactionsSeeMore() {
    this.track({
      event_name: 'click',
      feature: 'Transactions_see_more',
      event_category: EventCategory.SelfCare,
      page: Page.Dashboard,
      page_category: PageCategory.Dashboard,
      event_label: '/transactions',
    });
  },
  trackClickDashboardPackagesSeeMore() {
    this.track({
      event_name: 'click',
      feature: 'Packages_see_more',
      event_category: EventCategory.SelfCare,
      page: Page.Dashboard,
      page_category: PageCategory.Dashboard,
      event_label: '/packages',
    });
  },
  trackClickDashboardProductsSeeMore() {
    this.track({
      event_name: 'click',
      feature: 'Products_see_more',
      event_category: EventCategory.SelfCare,
      page: Page.Dashboard,
      page_category: PageCategory.Dashboard,
      event_label: '/products',
    });
  },
  trackClickDashboardProductsCarousel(product: string, position: number) {
    this.track({
      event_name: 'click',
      feature: 'Products_button_carousel',
      event_category: EventCategory.SelfCare,
      page: Page.Dashboard,
      page_category: PageCategory.Dashboard,
      event_label: '/products',
      product,
      position,
    });
  },
  trackClickDashboardLocationsSeeMore() {
    this.track({
      event_name: 'click',
      feature: 'Locations_see_more',
      event_category: EventCategory.SelfCare,
      page: Page.Dashboard,
      page_category: PageCategory.Dashboard,
      event_label: '/locations',
    });
  },
  trackClickDashboardEmployeesSeeMore() {
    this.track({
      event_name: 'click',
      feature: 'Employees_see_more',
      event_category: EventCategory.SelfCare,
      page: Page.Dashboard,
      page_category: PageCategory.Dashboard,
      event_label: '/employees',
    });
  },
  /* CONTRACT SELECTION */
  trackClickContractSelectionScreenView() {
    this.track({
      event_name: 'click',
      feature: 'Screen_view',
      event_category: EventCategory.SelfCare,
      page: Page.ContractSelection,
      page_category: PageCategory.Contracts,
      event_label: '/',
    });
  },
  trackClickContractSelectionContractsSearch(search_text: string) {
    this.track({
      event_name: 'click',
      feature: 'Contracts_search',
      event_category: EventCategory.SelfCare,
      page: Page.ContractSelection,
      page_category: PageCategory.Contracts,
      event_label: '/',
      search_text,
    });
  },
  trackClickContractSelectionContractsContinue(
    contract?: string,
    contract_position?: number,
    contract_status?: string,
  ) {
    this.track({
      event_name: 'click',
      feature: 'Contracts_continue',
      event_category: EventCategory.SelfCare,
      page: Page.ContractSelection,
      page_category: PageCategory.Contracts,
      event_label: '/contract',
      contract,
      contract_position,
      contract_status,
    });
  },
  /* CONTRACT PAGE */
  trackClickContractScreenView() {
    this.track({
      event_name: 'click',
      feature: 'Screen_view',
      event_category: EventCategory.SelfCare,
      page: Page.Contract,
      page_category: PageCategory.Contracts,
      event_label: '/contract',
    });
  },
  trackClickContractEditCompany() {
    this.track({
      event_name: 'click',
      feature: 'Edit_company',
      event_category: EventCategory.SelfCare,
      page: Page.Contract,
      page_category: PageCategory.Contracts,
      event_label: '/contract',
    });
  },
  trackClickContractEditInvoice() {
    this.track({
      event_name: 'click',
      feature: 'Edit_invoice',
      event_category: EventCategory.SelfCare,
      page: Page.Contract,
      page_category: PageCategory.Contracts,
      event_label: '/contract',
    });
  },
  trackClickContractEditServices() {
    this.track({
      event_name: 'click',
      feature: 'Edit_services',
      event_category: EventCategory.SelfCare,
      page: Page.Contract,
      page_category: PageCategory.Contracts,
      event_label: '/contract',
    });
  },
  trackClickContractEditHeadquartersAddress() {
    this.track({
      event_name: 'click',
      feature: 'Edit_headquarters_address',
      event_category: EventCategory.SelfCare,
      page: Page.Contract,
      page_category: PageCategory.Contracts,
      event_label: '/contract',
    });
  },
  trackClickContractEditCorrespondenceAddress() {
    this.track({
      event_name: 'click',
      feature: 'Edit_correspondence_address',
      event_category: EventCategory.SelfCare,
      page: Page.Contract,
      page_category: PageCategory.Contracts,
      event_label: '/contract',
    });
  },
  trackClickContractGeneratePdf() {
    this.track({
      event_name: 'click',
      feature: 'Generate_pdf',
      event_category: EventCategory.SelfCare,
      page: Page.Contract,
      page_category: PageCategory.Contracts,
      event_label: '/contract',
    });
  },
  /* CONTRACT PAGE: Your company section */
  trackClickCompanyEditSave() {
    this.track({
      event_name: 'click',
      feature: 'save',
      event_category: EventCategory.SelfCare,
      page: Page.CompanyEdit,
      page_category: PageCategory.Contracts,
      event_label: '/contract',
    });
  },
  trackClickCompanyEditCancel() {
    this.track({
      event_name: 'click',
      feature: 'cancel',
      event_category: EventCategory.SelfCare,
      page: Page.CompanyEdit,
      page_category: PageCategory.Contracts,
      event_label: '/contract',
    });
  },
  /* CONTRACT PAGE: Invoicing section */
  trackClickInvoiceEditSave() {
    this.track({
      event_name: 'click',
      feature: 'save',
      event_category: EventCategory.SelfCare,
      page: Page.InvoiceEdit,
      page_category: PageCategory.Contracts,
      event_label: '/contract',
    });
  },
  trackClickInvoiceEditCancel() {
    this.track({
      event_name: 'click',
      feature: 'cancel',
      event_category: EventCategory.SelfCare,
      page: Page.InvoiceEdit,
      page_category: PageCategory.Contracts,
      event_label: '/contract',
    });
  },
  /* CONTRACT PAGE: Services section */
  trackClickServicesEditSave(_: never, values: ServicesFormValues) {
    this.track({
      event_name: 'click',
      feature: 'save',
      event_category: EventCategory.SelfCare,
      page: Page.ServicesEdit,
      page_category: PageCategory.Contracts,
      event_label: '/contract',
      services_selected: values.serviceNames,
    });
  },
  trackClickServicesEditCancel() {
    this.track({
      event_name: 'click',
      feature: 'cancel',
      event_category: EventCategory.SelfCare,
      page: Page.ServicesEdit,
      page_category: PageCategory.Contracts,
      event_label: '/contract',
    });
  },
  trackClickCompanyServiceProductRemovalScreenView(products: string[]) {
    this.track({
      event_name: 'click',
      feature: 'screen_view',
      event_category: EventCategory.SelfCare,
      page: Page.CompanyServiceProductRemoval,
      page_category: PageCategory.Contracts,
      event_label: '/contract',
      products,
    });
  },
  trackClickCompanyServiceProductRemovalGotoProductOverview(products: string[]) {
    this.track({
      event_name: 'click',
      feature: 'goto_product_overview',
      event_category: EventCategory.SelfCare,
      page: Page.CompanyServiceProductRemoval,
      page_category: PageCategory.Contracts,
      event_label: '/contract',
      products,
    });
  },
  trackClickCompanyServiceProductRemovalCancel() {
    this.track({
      event_name: 'click',
      feature: 'cancel',
      event_category: EventCategory.SelfCare,
      page: Page.CompanyServiceProductRemoval,
      page_category: PageCategory.Contracts,
      event_label: '/contract',
    });
  },
  /* CONTRACT PAGE: Headquarters & Correspondence section */
  trackClickHeadquartersAddressEditSave() {
    this.track({
      event_name: 'click',
      feature: 'save',
      event_category: EventCategory.SelfCare,
      page: Page.HeadquartersAddressEdit,
      page_category: PageCategory.Contracts,
      event_label: '/contract',
    });
  },
  trackClickHeadquartersAddressEditCancel() {
    this.track({
      event_name: 'click',
      feature: 'cancel',
      event_category: EventCategory.SelfCare,
      page: Page.HeadquartersAddressEdit,
      page_category: PageCategory.Contracts,
      event_label: '/contract',
    });
  },
  trackClickCorrespondenceAddressEditSave() {
    this.track({
      event_name: 'click',
      feature: 'save',
      event_category: EventCategory.SelfCare,
      page: Page.CorrespondenceAddressEdit,
      page_category: PageCategory.Contracts,
      event_label: '/contract',
    });
  },
  trackClickCorrespondenceAddressEditCancel() {
    this.track({
      event_name: 'click',
      feature: 'cancel',
      event_category: EventCategory.SelfCare,
      page: Page.CorrespondenceAddressEdit,
      page_category: PageCategory.Contracts,
      event_label: '/contract',
    });
  },
  /* PRODUCTS PAGE */
  trackClickProductsScreenView() {
    this.track({
      event_name: 'click',
      feature: 'screen_view',
      event_category: EventCategory.SelfCare,
      page: Page.Product,
      page_category: PageCategory.Product,
      event_label: '/products',
    });
  },
  trackClickProductsProductEdit(product: string) {
    this.track({
      event_name: 'click',
      feature: 'product_edit',
      event_category: EventCategory.SelfCare,
      page: Page.Product,
      page_category: PageCategory.Product,
      event_label: '/products',
      product,
    });
  },
  trackClickProductsProductSeeOffer(product: string) {
    this.track({
      event_name: 'click',
      feature: 'product_see_offer',
      event_category: EventCategory.SelfCare,
      page: Page.Product,
      page_category: PageCategory.Product,
      event_label: '/products',
      product,
    });
  },
  trackClickProductDetailScreenView(product: string) {
    this.track({
      event_name: 'click',
      feature: 'screen_view',
      event_category: EventCategory.SelfCare,
      page: Page.ProductDetail,
      page_category: PageCategory.Product,
      event_label: '/products',
      product,
    });
  },
  trackClickProductDetailRemove(product: string) {
    this.track({
      event_name: 'click',
      feature: 'product_detail_remove',
      event_category: EventCategory.SelfCare,
      page: Page.ProductDetail,
      page_category: PageCategory.Product,
      event_label: '/products',
      product,
    });
  },
  trackClickProductDetailManageLocations(product: string) {
    this.track({
      event_name: 'click',
      feature: 'product_detail_manage',
      event_category: EventCategory.SelfCare,
      page: Page.ProductDetail,
      page_category: PageCategory.Product,
      event_label: '/products',
      product,
    });
  },
  trackClickProductDetailPayoutFreqChange(product: string, frequency: string) {
    this.track({
      event_name: 'click',
      feature: 'product_detail_payout_freq_change',
      event_category: EventCategory.SelfCare,
      page: Page.ProductDetail,
      page_category: PageCategory.Product,
      event_label: '/products',
      product,
      frequency,
    });
  },
  trackClickProductDetailPayoutOption(
    product: string,
    _priceAndFee: PriceAndFees,
    frequency: string,
    payout_option: string,
    _product_tier?: string,
  ) {
    this.track({
      event_name: 'click',
      feature: 'product_detail_payout_options',
      event_category: EventCategory.SelfCare,
      page: Page.ProductDetail,
      page_category: PageCategory.Product,
      event_label: '/products',
      product,
      frequency,
      payout_option,
    });
  },
  trackClickProductDetailApply(product: string) {
    this.track({
      event_name: 'click',
      feature: 'product_detail_apply',
      event_category: EventCategory.SelfCare,
      page: Page.ProductDetail,
      page_category: PageCategory.Product,
      event_label: '/products',
      product,
    });
  },
  trackClickProductUpdateScreenView(product: string) {
    this.track({
      event_name: 'click',
      feature: 'screen_view',
      event_category: EventCategory.SelfCare,
      page: Page.ProductUpdate,
      page_category: PageCategory.Product,
      event_label: '/products',
      product,
    });
  },
  trackClickProductUpdateConfirm(product: string) {
    this.track({
      event_name: 'click',
      feature: 'confirm',
      event_category: EventCategory.SelfCare,
      page: Page.ProductUpdate,
      page_category: PageCategory.Product,
      event_label: '/products',
      product,
    });
  },
  trackClickProductUpdateCancel(product: string) {
    this.track({
      event_name: 'click',
      feature: 'cancel',
      event_category: EventCategory.SelfCare,
      page: Page.ProductUpdate,
      page_category: PageCategory.Product,
      event_label: '/products',
      product,
    });
  },
  trackClickProductDeleteScreenView(product: string) {
    this.track({
      event_name: 'click',
      feature: 'screen_view',
      event_category: EventCategory.SelfCare,
      page: Page.ProductDelete,
      page_category: PageCategory.Product,
      event_label: '/products',
      product,
    });
  },
  trackClickProductDeleteConfirm(product: string) {
    this.track({
      event_name: 'click',
      feature: 'confirm',
      event_category: EventCategory.SelfCare,
      page: Page.ProductDelete,
      page_category: PageCategory.Product,
      event_label: '/products',
      product,
    });
  },
  trackClickProductDeleteCancel(product: string) {
    this.track({
      event_name: 'click',
      feature: 'cancel',
      event_category: EventCategory.SelfCare,
      page: Page.ProductDelete,
      page_category: PageCategory.Product,
      event_label: '/products',
      product,
    });
  },
  trackClickProductAddScreenView(product: string) {
    this.track({
      event_name: 'click',
      feature: 'screen_view',
      event_category: EventCategory.SelfCare,
      page: Page.ProductAdd,
      page_category: PageCategory.Product,
      event_label: '/products',
      product,
    });
  },
  trackClickProductAddConfirm(product: string) {
    this.track({
      event_name: 'click',
      feature: 'confirm',
      event_category: EventCategory.SelfCare,
      page: Page.ProductAdd,
      page_category: PageCategory.Product,
      event_label: '/products',
      product,
    });
  },
  trackClickProductAddCancel(product: string) {
    this.track({
      event_name: 'click',
      feature: 'cancel',
      event_category: EventCategory.SelfCare,
      page: Page.ProductAdd,
      page_category: PageCategory.Product,
      event_label: '/products',
      product,
    });
  },
  /* LOCATIONS PAGE */
  trackClickLocationsScreenView() {
    this.track({
      event_name: 'click',
      feature: 'screen_view',
      event_category: EventCategory.SelfCare,
      page: Page.Location,
      page_category: PageCategory.Location,
      event_label: '/locations',
    });
  },
  trackClickLocationsView(view_selected: string) {
    this.track({
      event_name: 'click',
      feature: 'location_view',
      event_category: EventCategory.SelfCare,
      page: Page.Location,
      page_category: PageCategory.Location,
      event_label: '/locations',
      view_selected,
    });
  },
  trackClickLocationsExport() {
    this.track({
      event_name: 'click',
      feature: 'export',
      event_category: EventCategory.SelfCare,
      page: Page.Location,
      page_category: PageCategory.Location,
      event_label: '/locations',
    });
  },
  trackClickLocationsAddLocation() {
    this.track({
      event_name: 'click',
      feature: 'add_location',
      event_category: EventCategory.SelfCare,
      page: Page.Location,
      page_category: PageCategory.Location,
      event_label: '/locations/new',
    });
  },
  trackClickLocationsShowInactive() {
    this.track({
      event_name: 'click',
      feature: 'show_inactive',
      event_category: EventCategory.SelfCare,
      page: Page.Location,
      page_category: PageCategory.Location,
      event_label: '/locations',
    });
  },
  trackClickLocationsSearch(search_text: string) {
    this.track({
      event_name: 'click',
      feature: 'location_search',
      event_category: EventCategory.SelfCare,
      page: Page.Location,
      page_category: PageCategory.Location,
      event_label: '/locations',
      search_text,
    });
  },
  trackClickLocationsLocator(location: string) {
    this.track({
      event_name: 'click',
      feature: 'location_locator',
      event_category: EventCategory.SelfCare,
      page: Page.Location,
      page_category: PageCategory.Location,
      event_label: '/locations',
      location,
    });
  },
  trackClickLocationsDetails(locationId: string, location: string, position: number) {
    this.track({
      event_name: 'click',
      feature: 'location_details',
      event_category: EventCategory.SelfCare,
      page: Page.Location,
      page_category: PageCategory.Location,
      event_label: `/locations/${locationId}`,
      location,
      position, // starts from 1
    });
  },
  /* LOCATION DETAIL PAGE */
  trackClickLocationEditScreenView(locationId: string) {
    this.track({
      event_name: 'click',
      feature: 'screen_view',
      event_category: EventCategory.SelfCare,
      page: Page.LocationEdit,
      page_category: PageCategory.Location,
      event_label: `/locations/${locationId}`,
    });
  },
  trackClickLocationEditBusinessUnit(locationId: string) {
    this.track({
      event_name: 'click',
      feature: 'edit_business_unit',
      event_category: EventCategory.SelfCare,
      page: Page.LocationEdit,
      page_category: PageCategory.Location,
      event_label: `/locations/${locationId}`,
    });
  },
  trackClickLocationEditBusinessLocator(locationId: string) {
    this.track({
      event_name: 'click',
      feature: 'locator_business_unit',
      event_category: EventCategory.SelfCare,
      page: Page.LocationEdit,
      page_category: PageCategory.Location,
      event_label: `/locations/${locationId}`,
    });
  },
  trackClickLocationEditLocationActivities(locationId: string) {
    this.track({
      event_name: 'click',
      feature: 'edit_location_activities',
      event_category: EventCategory.SelfCare,
      page: Page.LocationEdit,
      page_category: PageCategory.Location,
      event_label: `/locations/${locationId}`,
    });
  },
  trackClickLocationEditAddress(locationId: string) {
    this.track({
      event_name: 'click',
      feature: 'edit_address',
      event_category: EventCategory.SelfCare,
      page: Page.LocationEdit,
      page_category: PageCategory.Location,
      event_label: `/locations/${locationId}`,
    });
  },
  trackClickLocationEditAcceptedProducts(locationId: string) {
    this.track({
      event_name: 'click',
      feature: 'edit_accepted_products',
      event_category: EventCategory.SelfCare,
      page: Page.LocationEdit,
      page_category: PageCategory.Location,
      event_label: `/locations/${locationId}`,
    });
  },
  trackClickLocationEditPosTerminals(locationId: string) {
    this.track({
      event_name: 'click',
      feature: 'edit_pos_terminals',
      event_category: EventCategory.SelfCare,
      page: Page.LocationEdit,
      page_category: PageCategory.Location,
      event_label: `/locations/${locationId}`,
    });
  },
  trackClickLocationEditAddTerminals(locationId: string) {
    this.track({
      event_name: 'click',
      feature: 'add_terminals',
      event_category: EventCategory.SelfCare,
      page: Page.LocationEdit,
      page_category: PageCategory.Location,
      event_label: `/locations/${locationId}`,
    });
  },
  trackClickLocationEditScanQrCode(locationId: string) {
    this.track({
      event_name: 'click',
      feature: 'scan_qr_code',
      event_category: EventCategory.SelfCare,
      page: Page.LocationEdit,
      page_category: PageCategory.Location,
      event_label: `/locations/${locationId}`,
    });
  },
  trackClickLocationEditDeactivateLocation(locationId: string) {
    this.track({
      event_name: 'click',
      feature: 'deactivate_location',
      event_category: EventCategory.SelfCare,
      page: Page.LocationEdit,
      page_category: PageCategory.Location,
      event_label: `/locations/${locationId}`,
    });
  },
  trackClickLocationEditReactivateLocation(locationId: string) {
    this.track({
      event_name: 'click',
      feature: 'reactivate_location',
      event_category: EventCategory.SelfCare,
      page: Page.LocationEdit,
      page_category: PageCategory.Location,
      event_label: `/locations/${locationId}`,
    });
  },
  /* LOCATION DETAIL PAGE: Business unit */
  trackClickLocationEditSaveBusinessUnit(locationId: string) {
    this.track({
      event_name: 'click',
      feature: 'save_business_unit',
      event_category: EventCategory.SelfCare,
      page: Page.LocationEdit,
      page_category: PageCategory.Location,
      event_label: `/locations/${locationId}`,
    });
  },
  trackClickLocationEditCancelBusinessUnit(locationId: string) {
    this.track({
      event_name: 'click',
      feature: 'cancel_business_unit',
      event_category: EventCategory.SelfCare,
      page: Page.LocationEdit,
      page_category: PageCategory.Location,
      event_label: `/locations/${locationId}`,
    });
  },
  /* LOCATION DETAIL PAGE: Location activities */
  trackClickLocationEditSaveLocationActivities(locationId: string) {
    this.track({
      event_name: 'click',
      feature: 'save_location_activities',
      event_category: EventCategory.SelfCare,
      page: Page.LocationEdit,
      page_category: PageCategory.Location,
      event_label: `/locations/${locationId}`,
    });
  },
  trackClickLocationEditCancelLocationActivities(locationId: string) {
    this.track({
      event_name: 'click',
      feature: 'cancel_location_activities',
      event_category: EventCategory.SelfCare,
      page: Page.LocationEdit,
      page_category: PageCategory.Location,
      event_label: `/locations/${locationId}`,
    });
  },
  trackClickLocationEditRemoveLocationActivities(locationId: string) {
    this.track({
      event_name: 'click',
      feature: 'remove_location_activities',
      event_category: EventCategory.SelfCare,
      page: Page.LocationEdit,
      page_category: PageCategory.Location,
      event_label: `/locations/${locationId}`,
    });
  },
  trackClickLocationEditAddLocationActivities(locationId: string) {
    this.track({
      event_name: 'click',
      feature: 'add_location_activities',
      event_category: EventCategory.SelfCare,
      page: Page.LocationEdit,
      page_category: PageCategory.Location,
      event_label: `/locations/${locationId}`,
    });
  },
  /* LOCATION DETAIL PAGE: Address */
  trackClickLocationEditSaveAddress(locationId: string) {
    this.track({
      event_name: 'click',
      feature: 'save_address',
      event_category: EventCategory.SelfCare,
      page: Page.LocationEdit,
      page_category: PageCategory.Location,
      event_label: `/locations/${locationId}`,
    });
  },
  trackClickLocationEditCancelAddress(locationId: string) {
    this.track({
      event_name: 'click',
      feature: 'cancel_address',
      event_category: EventCategory.SelfCare,
      page: Page.LocationEdit,
      page_category: PageCategory.Location,
      event_label: `/locations/${locationId}`,
    });
  },
  /* LOCATION DETAIL PAGE: Accepted Pluxee products */
  trackClickLocationEditSaveAcceptedProducts(locationId: string, values: ProductsFormValues) {
    this.track({
      event_name: 'click',
      feature: 'save_accepted_products',
      event_category: EventCategory.SelfCare,
      page: Page.LocationEdit,
      page_category: PageCategory.Location,
      event_label: `/locations/${locationId}`,
      products_selected: values.productNames,
    });
  },
  trackClickLocationEditCancelAcceptedProducts(locationId: string, values: ProductsFormValues) {
    this.track({
      event_name: 'click',
      feature: 'cancel_accepted_products',
      event_category: EventCategory.SelfCare,
      page: Page.LocationEdit,
      page_category: PageCategory.Location,
      event_label: `/locations/${locationId}`,
      products_selected: values.productNames,
    });
  },
  /* LOCATION DETAIL PAGE: POS terminals */
  trackClickLocationEditSavePosTerminal(locationId: string) {
    this.track({
      event_name: 'click',
      feature: 'save_pos_terminal',
      event_category: EventCategory.SelfCare,
      page: Page.LocationEdit,
      page_category: PageCategory.Location,
      event_label: `/locations/${locationId}`,
    });
  },
  trackClickLocationEditCancelPosTerminal(locationId: string) {
    this.track({
      event_name: 'click',
      feature: 'cancel_pos_terminal',
      event_category: EventCategory.SelfCare,
      page: Page.LocationEdit,
      page_category: PageCategory.Location,
      event_label: `/locations/${locationId}`,
    });
  },
  trackClickLocationEditBlockPosTerminal(locationId: string) {
    this.track({
      event_name: 'click',
      feature: 'block_pos_terminal',
      event_category: EventCategory.SelfCare,
      page: Page.LocationEdit,
      page_category: PageCategory.Location,
      event_label: `/locations/${locationId}`,
    });
  },
  // BlockPosTerminal: Cancel + Confirm skipped
  trackClickLocationEditChangeLocationPosTerminal(locationId: string) {
    this.track({
      event_name: 'click',
      feature: 'change_location_pos_terminal',
      event_category: EventCategory.SelfCare,
      page: Page.LocationEdit,
      page_category: PageCategory.Location,
      event_label: `/locations/${locationId}`,
    });
  },
  // ChangeLocationPosTerminal: Cancel + Confirm skipped
  trackClickLocationEditAddPosTerminal(locationId: string) {
    this.track({
      event_name: 'click',
      feature: 'add_pos_terminal',
      event_category: EventCategory.SelfCare,
      page: Page.LocationEdit,
      page_category: PageCategory.Location,
      event_label: `/locations/${locationId}`,
    });
  },
  trackClickLocationEditRemovePosTerminal(locationId: string) {
    this.track({
      event_name: 'click',
      feature: 'remove_pos_terminal',
      event_category: EventCategory.SelfCare,
      page: Page.LocationEdit,
      page_category: PageCategory.Location,
      event_label: `/locations/${locationId}`,
    });
  },
  // Deactivate location: Cancel + Confirm skipped
  // Activate location: Cancel + Confirm skipped
  /* LOCATION ADD PAGE */
  // Add new activity skipped
  // Remove new activity skipped
  // Add another terminal skipped
  trackClickLocationAddScreenView() {
    this.track({
      event_name: 'click',
      feature: 'screen_view',
      event_category: EventCategory.SelfCare,
      page: Page.LocationAdd,
      page_category: PageCategory.Location,
      event_label: '/locations/new',
    });
  },
  trackClickLocationAddLocationConfirm(params: {
    event_location_name: string;
    event_activity: string[][];
    event_accepted_product: string[];
    event_terminal_definition: Array<{ deviceType: string; products: string[] }>;
  }) {
    this.track({
      event_name: 'click',
      feature: 'location_confirm',
      event_category: EventCategory.SelfCare,
      page: Page.LocationAdd,
      page_category: PageCategory.Location,
      event_label: '/locations',
      ...{
        event_location_name: params.event_location_name || undefined,
        event_activity:
          params.event_activity
            .map((row) => row.join(separators.semicolon))
            .join(separators.pipe) || undefined,
        event_accepted_product: params.event_accepted_product.join(separators.pipe) || undefined,
        event_terminal_definition:
          params.event_terminal_definition
            .map((def) => `${def.deviceType}: ${def.products.join(separators.semicolon)}`)
            .join(separators.pipe) || undefined,
        event_terminal_created_number: params.event_terminal_definition.length,
      },
    });
  },
  // Unsaved changes cancel skipped
  // Unsaved changes leave page skipped
  /* EMPLOYEES PAGE */
  trackClickEmployeesScreenView() {
    this.track({
      event_name: 'click',
      feature: 'screen_view',
      event_category: EventCategory.SelfCare,
      page: Page.Employee,
      page_category: PageCategory.Employee,
      event_label: '/employees',
    });
  },
  trackClickEmployeesView(view_selected: string) {
    this.track({
      event_name: 'click',
      feature: 'employee_view',
      event_category: EventCategory.SelfCare,
      page: Page.Employee,
      page_category: PageCategory.Employee,
      event_label: '/employees',
      view_selected,
    });
  },
  trackClickEmployeesAddEmployee() {
    this.track({
      event_name: 'click',
      feature: 'add_employee',
      event_category: EventCategory.SelfCare,
      page: Page.Employee,
      page_category: PageCategory.Employee,
      event_label: '/employees/new',
    });
  },
  trackClickEmployeesShowInactive() {
    this.track({
      event_name: 'click',
      feature: 'show_inactive',
      event_category: EventCategory.SelfCare,
      page: Page.Employee,
      page_category: PageCategory.Employee,
      event_label: '/employees',
    });
  },
  trackClickEmployeesSearch(search_text: string) {
    this.track({
      event_name: 'click',
      feature: 'employee_search',
      event_category: EventCategory.SelfCare,
      page: Page.Employee,
      page_category: PageCategory.Employee,
      event_label: '/employees',
      search_text,
    });
  },
  trackClickEmployeesDetails(employeeId: string, employee: string, position: number) {
    this.track({
      event_name: 'click',
      feature: 'employee_details',
      event_category: EventCategory.SelfCare,
      page: Page.Employee,
      page_category: PageCategory.Employee,
      event_label: `/employees/${employeeId}`,
      position: position + 1,
      employee,
    });
  },
  /* EMPLOYEE DETAIL PAGE */
  trackClickEmployeeEditScreenView(employeeId: string) {
    this.track({
      event_name: 'click',
      feature: 'screen_view',
      event_category: EventCategory.SelfCare,
      page: Page.EmployeeEdit,
      page_category: PageCategory.Employee,
      event_label: `/employees/${employeeId}`,
    });
  },
  trackClickEmployeeEditPersonalData(employeeId: string) {
    this.track({
      event_name: 'click',
      feature: 'edit_personal_data',
      event_category: EventCategory.SelfCare,
      page: Page.EmployeeEdit,
      page_category: PageCategory.Employee,
      event_label: `/employees/${employeeId}`,
    });
  },
  trackClickEmployeeEditContactPerson(employeeId: string) {
    this.track({
      event_name: 'click',
      feature: 'edit_contact_person',
      event_category: EventCategory.SelfCare,
      page: Page.EmployeeEdit,
      page_category: PageCategory.Employee,
      event_label: `/employees/${employeeId}`,
    });
  },
  trackClickEmployeeEditPortalAccess(employeeId: string) {
    this.track({
      event_name: 'click',
      feature: 'edit_portal_access',
      event_category: EventCategory.SelfCare,
      page: Page.EmployeeEdit,
      page_category: PageCategory.Employee,
      event_label: `/employees/${employeeId}`,
    });
  },
  trackClickEmployeeEditAssignedLocations(employeeId: string) {
    this.track({
      event_name: 'click',
      feature: 'edit_assigned_locations',
      event_category: EventCategory.SelfCare,
      page: Page.EmployeeEdit,
      page_category: PageCategory.Employee,
      event_label: `/employees/${employeeId}`,
    });
  },
  trackClickEmployeeEditDeactivateEmployee(employeeId: string) {
    this.track({
      event_name: 'click',
      feature: 'deactivate_employee',
      event_category: EventCategory.SelfCare,
      page: Page.EmployeeEdit,
      page_category: PageCategory.Employee,
      event_label: `/employees/${employeeId}`,
    });
  },
  trackClickEmployeeEditReactivateEmployee(employeeId: string) {
    this.track({
      event_name: 'click',
      feature: 'reactivate_employee',
      event_category: EventCategory.SelfCare,
      page: Page.EmployeeEdit,
      page_category: PageCategory.Employee,
      event_label: `/employees/${employeeId}`,
    });
  },
  trackClickEmployeeEditSavePersonalData(employeeId: string) {
    this.track({
      event_name: 'click',
      feature: 'save_personal_data',
      event_category: EventCategory.SelfCare,
      page: Page.EmployeeEdit,
      page_category: PageCategory.Employee,
      event_label: `/employees/${employeeId}`,
    });
  },
  trackClickEmployeeEditCancelPersonalData(employeeId: string) {
    this.track({
      event_name: 'click',
      feature: 'cancel_personal_data',
      event_category: EventCategory.SelfCare,
      page: Page.EmployeeEdit,
      page_category: PageCategory.Employee,
      event_label: `/employees/${employeeId}`,
    });
  },
  trackClickEmployeeEditSaveContactPerson(employeeId: string) {
    this.track({
      event_name: 'click',
      feature: 'save_contact_person',
      event_category: EventCategory.SelfCare,
      page: Page.EmployeeEdit,
      page_category: PageCategory.Employee,
      event_label: `/employees/${employeeId}`,
    });
  },
  trackClickEmployeeEditCancelContactPerson(employeeId: string) {
    this.track({
      event_name: 'click',
      feature: 'cancel_contact_person',
      event_category: EventCategory.SelfCare,
      page: Page.EmployeeEdit,
      page_category: PageCategory.Employee,
      event_label: `/employees/${employeeId}`,
    });
  },
  trackClickEmployeeEditSavePortalAccess(employeeId: string) {
    this.track({
      event_name: 'click',
      feature: 'save_portal_access',
      event_category: EventCategory.SelfCare,
      page: Page.EmployeeEdit,
      page_category: PageCategory.Employee,
      event_label: `/employees/${employeeId}`,
    });
  },
  trackClickEmployeeEditCancelPortalAccess(employeeId: string) {
    this.track({
      event_name: 'click',
      feature: 'cancel_portal_access',
      event_category: EventCategory.SelfCare,
      page: Page.EmployeeEdit,
      page_category: PageCategory.Employee,
      event_label: `/employees/${employeeId}`,
    });
  },
  // See permissions close skipped
  // Send invitation cancel skipped
  // Send invite skipped
  trackClickEmployeeEditSaveAssignedLocations(employeeId: string) {
    this.track({
      event_name: 'click',
      feature: 'save_assigned_locations',
      event_category: EventCategory.SelfCare,
      page: Page.EmployeeEdit,
      page_category: PageCategory.Employee,
      event_label: `/employees/${employeeId}`,
    });
  },
  trackClickEmployeeEditCancelAssignedLocations(employeeId: string) {
    this.track({
      event_name: 'click',
      feature: 'cancel_assigned_locations',
      event_category: EventCategory.SelfCare,
      page: Page.EmployeeEdit,
      page_category: PageCategory.Employee,
      event_label: `/employees/${employeeId}`,
    });
  },
  // Reactivate employee dialog cancel skipped
  // Reactivate employee dialog confirm skipped
  // Deactivate employee dialog cancel skipped
  // Deactivate employee dialog confirm skipped
  /* EMPLOYEE ADD PAGE */
  // See permissions skipped
  trackClickEmployeeAddScreenView() {
    this.track({
      event_name: 'click',
      feature: 'screen_view',
      event_category: EventCategory.SelfCare,
      page: Page.EmployeeAdd,
      page_category: PageCategory.Employee,
      event_label: '/employees/new',
    });
  },
  trackClickEmployeeAddConfirm(firstName: string, lastName: string) {
    this.track({
      event_name: 'click',
      feature: 'employee_confirm',
      event_category: EventCategory.SelfCare,
      page: Page.EmployeeAdd,
      page_category: PageCategory.Employee,
      event_label: '/employees',
      employee_name: firstName,
      employee_last_name: lastName,
    });
  },
  // Unsaved changes dialog cancel skipped
  // Unsaved changes dialog leave page skipped
  /* BAM: TRANSACTIONS PAGE */
  trackClickTransactionsScreenView() {
    this.track({
      event_name: 'click',
      feature: 'screen_view',
      event_category: EventCategory.SelfCare,
      page: Page.Transactions,
      page_category: PageCategory.BusinessActivity,
      event_label: '/transactions',
    });
  },
  trackClickTransactionsEditFilters() {
    this.track({
      event_name: 'click',
      feature: 'edit_filters',
      event_category: EventCategory.SelfCare,
      page: Page.Transactions,
      page_category: PageCategory.BusinessActivity,
      event_label: '/transactions',
    });
  },
  trackClickTransactionsCustomize() {
    this.track({
      event_name: 'click',
      feature: 'customize',
      event_category: EventCategory.SelfCare,
      page: Page.Transactions,
      page_category: PageCategory.BusinessActivity,
      event_label: '/transactions',
    });
  },
  trackClickTransactionsExport() {
    this.track({
      event_name: 'click',
      feature: 'export',
      event_category: EventCategory.SelfCare,
      page: Page.Transactions,
      page_category: PageCategory.BusinessActivity,
      event_label: '/transactions',
    });
  },
  /* BAM: INVOICES PAGE  */
  trackClickInvoicesScreenView() {
    this.track({
      event_name: 'click',
      feature: 'screen_view',
      event_category: EventCategory.SelfCare,
      page: Page.Invoices,
      page_category: PageCategory.BusinessActivity,
      event_label: '/invoices',
    });
  },
  trackClickInvoicesEditFilters() {
    this.track({
      event_name: 'click',
      feature: 'edit_filters',
      event_category: EventCategory.SelfCare,
      page: Page.Invoices,
      page_category: PageCategory.BusinessActivity,
      event_label: '/invoices',
    });
  },
  trackClickInvoicesCustomize() {
    this.track({
      event_name: 'click',
      feature: 'customize',
      event_category: EventCategory.SelfCare,
      page: Page.Invoices,
      page_category: PageCategory.BusinessActivity,
      event_label: '/invoices',
    });
  },
  trackClickInvoicesExport() {
    this.track({
      event_name: 'click',
      feature: 'export',
      event_category: EventCategory.SelfCare,
      page: Page.Invoices,
      page_category: PageCategory.BusinessActivity,
      event_label: '/invoices',
    });
  },
  /* BAM: PACKAGES PAGE */
  trackClickPackagesScreenView() {
    this.track({
      event_name: 'click',
      feature: 'screen_view',
      event_category: EventCategory.SelfCare,
      page: Page.Packages,
      page_category: PageCategory.BusinessActivity,
      event_label: '/packages',
    });
  },
  trackClickPackagesEditFilters() {
    this.track({
      event_name: 'click',
      feature: 'edit_filters',
      event_category: EventCategory.SelfCare,
      page: Page.Packages,
      page_category: PageCategory.BusinessActivity,
      event_label: '/packages',
    });
  },
  trackClickPackagesCustomize() {
    this.track({
      event_name: 'click',
      feature: 'customize',
      event_category: EventCategory.SelfCare,
      page: Page.Packages,
      page_category: PageCategory.BusinessActivity,
      event_label: '/packages',
    });
  },
  trackClickPackagesExport() {
    this.track({
      event_name: 'click',
      feature: 'export',
      event_category: EventCategory.SelfCare,
      page: Page.Packages,
      page_category: PageCategory.BusinessActivity,
      event_label: '/packages',
    });
  },
  /* BAM: PERFORMANCE REPORT PAGE */
  trackClickPerformanceReportScreenView() {
    this.track({
      event_name: 'click',
      feature: 'screen_view',
      event_category: EventCategory.SelfCare,
      page: Page.PerformanceReport,
      page_category: PageCategory.BusinessActivity,
      event_label: '/performance',
    });
  },
  trackClickPerformanceReportEditFilters() {
    this.track({
      event_name: 'click',
      feature: 'edit_filters',
      event_category: EventCategory.SelfCare,
      page: Page.PerformanceReport,
      page_category: PageCategory.BusinessActivity,
      event_label: '/performance',
    });
  },
  trackClickPerformanceReportCustomize() {
    this.track({
      event_name: 'click',
      feature: 'customize',
      event_category: EventCategory.SelfCare,
      page: Page.PerformanceReport,
      page_category: PageCategory.BusinessActivity,
      event_label: '/performance',
    });
  },
  trackClickPerformanceReportExport() {
    this.track({
      event_name: 'click',
      feature: 'export',
      event_category: EventCategory.SelfCare,
      page: Page.PerformanceReport,
      page_category: PageCategory.BusinessActivity,
      event_label: '/performance',
    });
  },
  /* ONLINE PAYMENTS PAGE */
  trackClickPaymentsStep1ScreenView() {
    this.track({
      event_name: 'click',
      feature: 'screen_view',
      event_category: EventCategory.SelfCare,
      page: Page.PaymentsStep1,
      page_category: PageCategory.Payments,
      event_label: '/payments',
    });
  },
  trackClickPaymentsStep1Confirm() {
    this.track({
      event_name: 'click',
      feature: 'payment_confirm',
      event_category: EventCategory.SelfCare,
      page: Page.PaymentsStep1,
      page_category: PageCategory.Payments,
      event_label: '/payments',
    });
  },
  trackClickPaymentsStep2ScreenView() {
    this.track({
      event_name: 'click',
      feature: 'screen_view',
      event_category: EventCategory.SelfCare,
      page: Page.PaymentsStep2,
      page_category: PageCategory.Payments,
      event_label: '/payments',
    });
  },
  trackClickPaymentsStep2Confirm() {
    this.track({
      event_name: 'click',
      feature: 'payment_confirm',
      event_category: EventCategory.SelfCare,
      page: Page.PaymentsStep2,
      page_category: PageCategory.Payments,
      event_label: '/payments',
    });
  },
  trackClickPaymentsStep3ScreenView() {
    this.track({
      event_name: 'click',
      feature: 'screen_view',
      event_category: EventCategory.SelfCare,
      page: Page.PaymentsStep3,
      page_category: PageCategory.Payments,
      event_label: '/payments',
    });
  },
  /* REMITTANCE SHEET PAGE */
  trackClickRemittanceSheetDetailScreenView() {
    this.track({
      event_name: 'click',
      feature: 'screen_view',
      event_category: EventCategory.SelfCare,
      page: Page.RemittanceSheetDetail,
      page_category: PageCategory.RemittanceSheet,
      event_label: '/remittance-sheet',
    });
  },
  trackClickRemittanceSheetDetailSeeArchive() {
    this.track({
      event_name: 'click',
      feature: 'see_archive',
      event_category: EventCategory.SelfCare,
      page: Page.RemittanceSheetDetail,
      page_category: PageCategory.RemittanceSheet,
      event_label: '/remittance-sheet/archive',
    });
  },
  trackClickRemittanceSheetDetailSavePdf() {
    this.track({
      event_name: 'click',
      feature: 'save_pdf',
      event_category: EventCategory.SelfCare,
      page: Page.RemittanceSheetDetail,
      page_category: PageCategory.RemittanceSheet,
      event_label: '/remittance-sheet',
    });
  },
  trackClickRemittanceSheetDetailCreateBlankPdf() {
    this.track({
      event_name: 'click',
      feature: 'create_blank_pdf',
      event_category: EventCategory.SelfCare,
      page: Page.RemittanceSheetDetail,
      page_category: PageCategory.RemittanceSheet,
      event_label: '/remittance-sheet',
    });
  },
  /* REMITTANCE SHEET ARCHIVE PAGE */
  trackClickRemittanceSheetArchiveScreenView() {
    this.track({
      event_name: 'click',
      feature: 'screen_view',
      event_category: EventCategory.SelfCare,
      page: Page.RemittanceSheetArchive,
      page_category: PageCategory.RemittanceSheet,
      event_label: '/remittance-sheet/archive',
    });
  },
  trackClickRemittanceSheetArchiveEditFilters() {
    this.track({
      event_name: 'click',
      feature: 'edit_filters',
      event_category: EventCategory.SelfCare,
      page: Page.RemittanceSheetArchive,
      page_category: PageCategory.RemittanceSheet,
      event_label: '/remittance-sheet/archive',
    });
  },
  trackClickRemittanceSheetArchiveExport() {
    this.track({
      event_name: 'click',
      feature: 'export',
      event_category: EventCategory.SelfCare,
      page: Page.RemittanceSheetArchive,
      page_category: PageCategory.RemittanceSheet,
      event_label: '/remittance-sheet/archive',
    });
  },
  trackClickRemittanceSheetArchiveDeleteSheet() {
    this.track({
      event_name: 'click',
      feature: 'delete',
      event_category: EventCategory.SelfCare,
      page: Page.RemittanceSheetArchive,
      page_category: PageCategory.RemittanceSheet,
      event_label: '/remittance-sheet/archive',
    });
  },
  /* Bulgarian specific events */
  trackClickInvoicesDownloadInvoice(
    event_invoice_name: string,
    event_invoice_file_name: string,
    event_invoice_date: string,
  ) {
    this.track({
      event_name: 'click',
      feature: 'download_invoice',
      event_category: EventCategory.SelfCare,
      page: Page.Invoices,
      page_category: PageCategory.BusinessActivity,
      event_invoice_name,
      event_invoice_file_name,
      event_invoice_date,
      event_label: '/invoices',
    });
  },
  trackClickInvoicesDownloadTransactionsList(
    event_invoice_name: string,
    event_transactions_file_name: string,
    event_invoice_date: string,
  ) {
    this.track({
      event_name: 'click',
      feature: 'download_transactions_list',
      event_category: EventCategory.SelfCare,
      page: Page.Invoices,
      page_category: PageCategory.BusinessActivity,
      event_invoice_name,
      event_transactions_file_name,
      event_invoice_date,
      event_label: '/invoices',
    });
  },
};

bindMethods(gtm);

export default gtm;
